import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle, XCircle, AlertCircle, Star, User, Calendar, Tag, Award, FileText, ExternalLink, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getImageUrl } from '@/services/api';

const DetailItem = ({ icon: Icon, label, value, className = '' }) => (
  <div className="flex items-start">
    <Icon className={`w-4 h-4 text-gray-500 mt-1 mr-3 flex-shrink-0 ${className}`} />
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="font-medium text-gray-800">{value || '-'}</p>
    </div>
  </div>
);

const getStatusInfo = (status, decision, reviewStatus) => {
  const statusVal = reviewStatus || status || decision;
  if (statusVal === 'pending' || !statusVal) {
    return { text: 'รอการประเมิน', icon: AlertCircle, color: 'text-yellow-600' };
  }
  if (statusVal === 'accepted') return { text: 'ตอบรับ', icon: CheckCircle, color: 'text-green-600' };
  if (statusVal === 'rejected') return { text: 'ปฏิเสธ', icon: XCircle, color: 'text-red-600' };
  if (statusVal === 'conditional_accept') return { text: 'ตอบรับแบบมีเงื่อนไข', icon: AlertCircle, color: 'text-blue-600' };
  return { text: 'ตรวจสอบแล้ว', icon: CheckCircle, color: 'text-gray-600' };
};

const SubmissionDetailModal = ({ isOpen, onOpenChange, submission }) => {
  if (!submission) return null;

  // แมปข้อมูลจาก registrations
  const title = submission.project_title || submission.title || submission.work_title ||
    (submission.research_data && typeof submission.research_data === 'object' && submission.research_data.project_title) ||
    (typeof submission.research_data === 'string' && (() => {
      try {
        const d = JSON.parse(submission.research_data);
        return d?.project_title;
      } catch { return null; }
    })()) ||
    'ไม่ระบุชื่อผลงาน';
  const author = `${submission.first_name || ''} ${submission.last_name || ''}`.trim() || submission.author || '-';
  const type = submission.registration_type === 'research' ? 'ผลงานวิชาการ' :
    submission.registration_type === 'creative' ? 'ผลงานสร้างสรรค์' : submission.registration_type || submission.type || '-';
  const submissionDate = submission.created_at
    ? new Date(submission.created_at).toLocaleDateString('th-TH')
    : submission.submissionDate || '-';
  const score = submission.review_score ?? submission.score;
  const reviewer = submission.reviewer_name || submission.reviewer || '-';
  const conditions = submission.review_conditions || submission.conditions;
  const comments = submission.review_comments || submission.comments;
  const statusInfo = getStatusInfo(submission.status, submission.review_decision, submission.review_status);
  const StatusIcon = statusInfo.icon;

  // URL ไฟล์ PDF (research_file_url อาจเป็น relative หรือ full URL)
  const pdfUrl = submission.research_file_url
    ? (submission.research_file_url.startsWith('http') ? submission.research_file_url : getImageUrl(submission.research_file_url))
    : null;

  const handleDownloadPdf = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.download = `${title}_document.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ดึงบทคัดย่อจาก research_data
  let abstract = null;
  try {
    const rd = typeof submission.research_data === 'string' ? JSON.parse(submission.research_data || '{}') : (submission.research_data || {});
    abstract = rd.abstract || rd.project_title;
  } catch { /* ignore */ }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-800">{title}</DialogTitle>
          <DialogDescription>
            รายละเอียดผลงานที่ส่งเข้าประกวด/นำเสนอ
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4 overflow-y-auto pr-4 flex-1">
          <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
            <h3 className="font-semibold text-lg mb-4 text-gray-700">ข้อมูลผลงาน</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <DetailItem icon={User} label="ผู้ส่งผลงาน" value={author} />
              <DetailItem icon={Tag} label="ประเภทผลงาน" value={type} />
              <DetailItem icon={Award} label="ชื่องาน" value={title} />
              <DetailItem icon={Calendar} label="วันที่ส่ง" value={submissionDate} />
              {submission.email && <DetailItem icon={FileText} label="อีเมล" value={submission.email} />}
            </div>
            {abstract && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-1">บทคัดย่อ</p>
                <p className="text-sm text-gray-700 bg-white p-3 rounded border">{abstract}</p>
              </div>
            )}
          </div>

          {/* ดู/ดาวน์โหลดไฟล์ PDF */}
          {pdfUrl && (
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <h3 className="font-semibold text-lg mb-4 text-gray-700">ไฟล์เอกสาร (PDF)</h3>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  เปิดดู PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPdf}
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  ดาวน์โหลดไฟล์
                </Button>
              </div>
              <div className="mt-3 rounded overflow-hidden border bg-white" style={{ height: 400 }}>
                <iframe
                  src={`${pdfUrl}#toolbar=1`}
                  title="ดูเอกสาร PDF"
                  className="w-full h-full"
                  style={{ minHeight: 360 }}
                />
              </div>
            </div>
          )}

          <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
            <h3 className="font-semibold text-lg mb-4 text-gray-700">ผลการประเมิน</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div className="flex items-start">
                <StatusIcon className={`w-4 h-4 ${statusInfo.color} mt-1 mr-3 flex-shrink-0`} />
                <div>
                  <p className="text-sm text-gray-500">สถานะ</p>
                  <p className={`font-bold ${statusInfo.color}`}>{statusInfo.text}</p>
                </div>
              </div>
              <DetailItem icon={Star} label="คะแนน" value={score} />
              <DetailItem icon={User} label="ผู้ประเมิน" value={reviewer} />
            </div>
            {conditions && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 flex items-center"><FileText className="w-3 h-3 mr-2"/>เงื่อนไขการตอบรับ</p>
                <p className="font-medium text-blue-800 bg-blue-50 p-3 rounded-md mt-1 text-sm">{conditions}</p>
              </div>
            )}
            {comments && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 flex items-center"><FileText className="w-3 h-3 mr-2"/>ความคิดเห็นเพิ่มเติม</p>
                <p className="font-medium text-gray-800 bg-gray-100 p-3 rounded-md mt-1 text-sm">{comments}</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubmissionDetailModal;
